import React from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import {
  AlertWarningIcon,
  Button,
  CheckIcon,
  IconButton,
  LightBulbIcon,
  Link,
  XCloseIcon
} from '@customink/pigment-react';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const DecorationTypeTips = ({
  callbackHandleClose,
  callbackHandleDone,
  callbackHandleHelpLink,
  callbackHandleProductLink,
  customClasses,
  description,
  dismiss,
  gradientDesignImg,
  product,
  oneColorDesignImage,
  title
}) => {
  const navigate = useNavigate();

  const handleMouseDown = e => {
    e.stopPropagation();
  };

  const handleProductLink = () => {
    dismiss();
    if (product?.primary_category_name && product?.primary_category_id) {
      navigate(
        `/productCatalog/${product.primary_category_name}/${product.primary_category_id}`
      );
    }
    if (callbackHandleProductLink) callbackHandleProductLink();
  };

  const handleClose = () => {
    dismiss();
    if (callbackHandleClose) callbackHandleClose();
  };

  const handleDone = () => {
    dismiss();
    if (callbackHandleDone) callbackHandleDone();
  };

  const handleHelpLink = () => {
    dismiss();
    navigate('/help');
    if (callbackHandleHelpLink) callbackHandleHelpLink();
  };

  return (
    <div
      className={`ndx-DecorationTypeTips ${customClasses}`}
      onMouseDown={handleMouseDown}>
      <IconButton
        onClick={handleClose}
        className="ndx-DecorationTypeTips-close"
        role="button"
        data-testid="close-button">
        <XCloseIcon />
      </IconButton>
      <div className="ndx-DecorationTypeTips-content">
        <h1>{title}</h1>
        <p className="ndx-DecorationTypeTips-content-summary">
          {description} or{' '}
          <Link onClick={handleHelpLink}>reach out for help.</Link>
        </p>
        <div className="ndx-DecorationTypeTips-content-imgs">
          <div className="ndx-DecorationTypeTips-content-imgs-container">
            <div className="ndx-DecorationTypeTips-content-imgs-container-header">
              <CheckIcon color="success" />
              <span>One-color artwork</span>
            </div>
            {oneColorDesignImage && (
              <img src={oneColorDesignImage} alt="one-color-design-img" />
            )}
          </div>
          <div className="ndx-DecorationTypeTips-content-imgs-container">
            <div className="ndx-DecorationTypeTips-content-imgs-container-header">
              <AlertWarningIcon color="red" />
              <span>Gradients or photos</span>
            </div>
            {gradientDesignImg && (
              <img src={gradientDesignImg} alt="gradient-design-img" />
            )}
          </div>
        </div>
        <div className="ndx-DecorationTypeTips-content-tip">
          <span>
            <LightBulbIcon className="ndx-DecorationTypeTips-content-tip-icon" />
            Our artists will also adjust your image for the best result.
          </span>
        </div>
        <div className="ndx-DecorationTypeTips-content-cta">
          <Button
            size="large"
            className="ndx-DecorationTypeTips-content-cta-primary"
            onClick={handleDone}>
            Got it!
          </Button>
          <Link
            className="ndx-DecorationTypeTips-content-cta-link"
            onClick={handleProductLink}>
            Find Similar Products With More Color Options.
          </Link>
        </div>
      </div>
    </div>
  );
};

DecorationTypeTips.displayName = 'DecorationTypeTips';
const propTypes = {
  callbackHandleClose: PropTypes.func,
  callbackHandleDone: PropTypes.func,
  callbackHandleHelpLink: PropTypes.func,
  callbackHandleProductLink: PropTypes.func,
  customClasses: PropTypes.string,
  description: PropTypes.string,
  dismiss: PropTypes.func,
  gradientDesignImg: PropTypes.string,
  oneColorDesignImage: PropTypes.string,
  product: PropTypes.object,
  title: PropTypes.string
};

DecorationTypeTips.propTypes = propTypes;

export default DecorationTypeTips;
